/**
 * "Load more" for collection grids, layered over real pagination.
 *
 * snippets/collection-load-more.liquid renders the numbered pagination and a
 * "Load more" link to the next page, so crawlers and visitors without JS page
 * through as before. This element:
 * - appends the next page's cards in place (Section Rendering API),
 * - keeps the URL at ?page=<last page on screen>, with the first page on
 *   screen in history.state,
 * - hides the page numbers once two pages are on screen,
 * - adds "Load previous" above the grid when the visit starts past page 1,
 * - on back/reload, loads the earlier pages again and scrolls to the card
 *   the shopper opened, so they come back to where they were.
 *
 * Filtering replaces #CollectionAjaxContent, which drops this element and
 * connects a fresh one for page 1.
 */
(function () {
  if (customElements.get('collection-load-more')) return;

  var STATE_KEY = 'collectionLoadMore';

  function pageUrl(page, sectionId) {
    var url = new URL(window.location.href);
    url.hash = '';
    if (page > 1) url.searchParams.set('page', String(page));
    else url.searchParams.delete('page');
    if (sectionId) url.searchParams.set('section_id', sectionId);
    return url;
  }

  /* theme.js starts AOS with its mutation observer off, so cards added after
     load never get aos-animate and their images stay hidden. Mark them the
     way AOS would. */
  function revealCards(cards) {
    cards.forEach(function (card) {
      card.classList.add('aos-init', 'aos-animate');
      card.querySelectorAll('[data-aos]').forEach(function (el) {
        el.classList.add('aos-init', 'aos-animate');
      });
    });
  }

  /* Loox draws stars once, for the ratings on the page when it ran, and has
     no public re-scan. Same markup it writes, using its icon sprite; without
     the sprite the cards keep the plain "N Reviews" text. (Same as
     wishlist.js refreshRatings.) */
  function refreshRatings(cards) {
    if (!document.getElementById('looxicons-rating-icon')) return;
    cards.forEach(function (card) {
      card.querySelectorAll('.loox-rating:not([data-rating-upgraded])').forEach(function (el) {
        var rating = parseFloat(el.getAttribute('data-rating')) || 0;
        var raters = parseInt(el.getAttribute('data-raters'), 10) || 0;
        if (!raters) return;
        var label = raters + ' reviews';
        var html = '<div class="loox-rating-content">';
        for (var n = 1; n <= 5; n++) {
          var fill = rating >= n ? 'full' : rating >= n - 0.5 ? 'half' : 'empty';
          html +=
            '<svg class="loox-icon" viewBox="0 0 24 24" data-lx-fill="' + fill + '" aria-hidden="true">' +
            (n === 1 ? '<title>' + label + '</title>' : '') +
            '<use href="#looxicons-rating-icon"></use></svg>';
        }
        html += '<span class="loox-rating-label" aria-hidden="true">&nbsp;(' + raters + ')</span></div>';
        el.insertAdjacentHTML('beforeend', html);
        el.setAttribute('data-rating-upgraded', '1');
        el.setAttribute('role', 'figure');
        el.setAttribute('aria-label', label);
      });
    });
  }

  /* theme.initQuickShop() binds every card on the page again, so do what it
     does for the new cards only: preload the quick view on first hover. */
  function bindQuickShop(cards) {
    var theme = window.theme;
    if (!theme || !theme.settings || !theme.settings.quickView) return;
    if (typeof theme.preloadProductModal !== 'function') return;
    cards.forEach(function (card) {
      if (!card.dataset.productId) return;
      var onEnter = function () {
        if (theme.config && theme.config.bpSmall) return;
        card.removeEventListener('mouseover', onEnter);
        card.removeEventListener('focusin', onEnter);
        theme.preloadProductModal(
          card.dataset.productHandle,
          card.dataset.productId,
          card.querySelector('.quick-product__btn')
        );
      };
      card.addEventListener('mouseover', onEnter);
      card.addEventListener('focusin', onEnter);
    });
  }

  class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
      var root = this.closest('#CollectionAjaxContent') || document;
      this.grid = root.querySelector('.collection-grid__wrapper .grid--uniform');
      if (!this.grid) return;

      this.sectionId = this.dataset.sectionId || '';
      this.pages = Number(this.dataset.pages) || 1;
      this.pageSize = Number(this.dataset.pageSize) || 32;
      this.total = Number(this.dataset.total) || 0;
      this.first = this.last = Number(this.dataset.page) || 1;
      this.busy = false;

      this.counter = this.querySelector('.collection-load-more__count');
      this.bar = this.querySelector('.collection-load-more__bar span');
      this.moreBtn = this.querySelector('[data-load-more]');
      this.pagesNav = this.querySelector('.collection-load-more__pages');

      if (this.moreBtn) {
        this.moreBtn.addEventListener('click', this.onMore.bind(this));
      }
      if (this.first > 1) this.addPrevButton();

      /* Remember which card was opened, so back can return to it. */
      this.onCardClick = this.onCardClick.bind(this);
      this.grid.addEventListener('click', this.onCardClick);

      this.restore();
    }

    disconnectedCallback() {
      if (this.grid) this.grid.removeEventListener('click', this.onCardClick);
      if (this.prevWrap && this.prevCreated) this.prevWrap.remove();
    }

    addPrevButton() {
      /* collection-grid renders it from the server; build one only if the
         grid snippet didn't (search, older templates). */
      var existing = this.grid.parentElement.querySelector('.collection-load-more--prev');
      if (existing) {
        this.prevWrap = existing;
        this.prevBtn = existing.querySelector('[data-load-prev]') || existing.querySelector('a');
        this.prevBtn.addEventListener('click', this.onPrev.bind(this));
        return;
      }
      this.prevCreated = true;
      this.prevWrap = document.createElement('div');
      this.prevWrap.className = 'collection-load-more collection-load-more--prev';
      this.prevBtn = document.createElement('a');
      this.prevBtn.className = 'btn btn--secondary collection-load-more__btn';
      this.prevBtn.href = pageUrl(this.first - 1).toString();
      this.prevBtn.rel = 'prev';
      this.prevBtn.textContent = this.dataset.prevLabel || 'Load previous';
      this.prevBtn.addEventListener('click', this.onPrev.bind(this));
      this.prevWrap.appendChild(this.prevBtn);
      this.grid.before(this.prevWrap);
    }

    onMore(event) {
      event.preventDefault();
      if (this.busy || this.last >= this.pages) return;
      this.load(this.last + 1, 'next', event.detail === 0);
    }

    onPrev(event) {
      event.preventDefault();
      if (this.busy || this.first <= 1) return;
      this.load(this.first - 1, 'prev', event.detail === 0);
    }

    async fetchCards(page) {
      var response = await fetch(pageUrl(page, this.sectionId).toString(), {
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      var doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      var grid = doc.querySelector('.collection-grid__wrapper .grid--uniform');
      if (!grid) throw new Error('No grid in response');

      var existing = new Set(
        Array.from(this.grid.querySelectorAll('.grid-product[data-product-id]')).map(function (el) {
          return el.dataset.productId;
        })
      );
      /* Product cards only: text cards and anything else in the grid belong
         to page 1. */
      return Array.from(grid.children).filter(function (el) {
        return el.matches('.grid-product') && !existing.has(el.dataset.productId);
      });
    }

    insert(cards, direction) {
      if (!cards.length) return;
      revealCards(cards);

      if (direction === 'prev') {
        /* Keep the cards already on screen where they are. Browsers with
           scroll anchoring already do this; then the correction is 0. */
        var anchor = this.grid.querySelector('.grid-product');
        var before = anchor ? anchor.getBoundingClientRect().top : 0;
        this.grid.prepend.apply(this.grid, cards);
        if (anchor) {
          var shift = anchor.getBoundingClientRect().top - before;
          if (shift) window.scrollBy(0, shift);
        }
      } else {
        this.grid.append.apply(this.grid, cards);
      }

      refreshRatings(cards);
      bindQuickShop(cards);
    }

    async load(page, direction, fromKeyboard) {
      var btn = direction === 'prev' ? this.prevBtn : this.moreBtn;
      this.busy = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');

      try {
        var cards = await this.fetchCards(page);
        this.insert(cards, direction);
        if (direction === 'prev') this.first = page;
        else this.last = page;
        this.update();

        /* Keyboard users go on from the first new card instead of being
           left on the button. */
        if (fromKeyboard && cards[0]) {
          var link = cards[0].querySelector('a[href]');
          if (link) link.focus({ preventScroll: direction === 'prev' });
        }

        document.dispatchEvent(
          new CustomEvent('collection:loadmore', { detail: { page: page, direction: direction } })
        );
      } catch (error) {
        /* Fall back to the real page rather than stranding the shopper. */
        window.location.assign(btn.href);
        return;
      } finally {
        this.busy = false;
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
    }

    update() {
      var shown = Math.min(this.last * this.pageSize, this.total);
      if (this.counter) {
        var template = this.counter.dataset.template || 'Showing [shown] of [total] products';
        this.counter.textContent = template
          .replace('[shown]', String(shown))
          .replace('[total]', String(this.total));
      }
      if (this.bar && this.total) {
        this.bar.style.width = Math.round((shown / this.total) * 100) + '%';
      }

      if (this.moreBtn) {
        if (this.last >= this.pages) this.moreBtn.hidden = true;
        else this.moreBtn.href = pageUrl(this.last + 1).toString();
      }
      if (this.prevWrap) {
        if (this.first <= 1) this.prevWrap.hidden = true;
        else this.prevBtn.href = pageUrl(this.first - 1).toString();
      }

      /* Page numbers stop making sense once pages are stacked. */
      if (this.pagesNav && this.last > this.first) this.pagesNav.hidden = true;

      this.saveState({ first: this.first, last: this.last });
    }

    saveState(patch) {
      var state = Object.assign({}, history.state);
      state[STATE_KEY] = Object.assign({}, state[STATE_KEY], patch);
      history.replaceState(state, '', pageUrl(this.last).toString() + window.location.hash);
    }

    onCardClick(event) {
      var card = event.target.closest('.grid-product[data-product-id]');
      if (!card || !event.target.closest('a[href]')) return;
      this.saveState({
        first: this.first,
        last: this.last,
        product: card.dataset.productId,
        offset: Math.round(card.getBoundingClientRect().top),
      });
    }

    /* Coming back (back button or reload) to ?page=N after loading from page
       F: load F..N-1 again, then put the opened card back where it was. */
    async restore() {
      var saved = history.state && history.state[STATE_KEY];
      if (!saved || Number(saved.last) !== this.last) return;
      var first = Math.max(1, Number(saved.first) || this.last);
      /* One page on screen: the browser's own scroll restoration is right. */
      if (first >= this.last) return;

      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      this.busy = true;
      try {
        if (first < this.last) {
          var pages = [];
          for (var p = first; p < this.last; p++) pages.push(p);
          var batches = await Promise.all(pages.map(this.fetchCards.bind(this)));
          var cards = [].concat.apply([], batches);
          this.insert(cards, 'prev');
          this.first = first;
        }
        this.update();
      } catch (error) {
        /* Page N alone still works; "Load previous" covers the rest. */
      } finally {
        this.busy = false;
      }

      var card = saved.product
        ? this.grid.querySelector('.grid-product[data-product-id="' + saved.product + '"]')
        : null;
      if (card) {
        var top = card.getBoundingClientRect().top + window.scrollY - (Number(saved.offset) || 0);
        window.scrollTo(0, Math.max(0, top));
      }
      /* scrollRestoration stays manual for this history entry: its state
         still spans several pages, so the next return comes through here
         again. */
    }
  }

  customElements.define('collection-load-more', CollectionLoadMore);
})();
